import React from "react";

type Block =
  | { type: "p"; text: string }
  | { type: "list"; items: string[] }
  | { type: "email" }
  | { type: "address"; lines: string[] };

interface Subsection {
  title: string;
  blocks: Block[];
}

interface Section {
  id: string;
  title: string;
  blocks: Block[];
  subsections?: Subsection[];
}

const CONTACT_EMAIL = "help@realesy.com";

const TITLE = "REALESY PRIVACY POLICY";

const intro: string[] = [
  "RealEsy, LLC (“RealEsy,” “we,” “us,” or “our”) respects your privacy and is committed to protecting personal information collected through the RealEsy platform.",
  "This Privacy Policy explains how RealEsy collects, uses, stores, discloses, and protects personal information when you access or use the RealEsy website, web-based platform, mobile applications, software, services, features, integrations, and related services (collectively, the “Services”).",
  "This Privacy Policy also explains certain choices and rights you may have concerning your personal information.",
  "By accessing or using the Services, you acknowledge the practices described in this Privacy Policy.",
];

const sections: Section[] = [
  {
    id: "scope",
    title: "1. Scope of This Privacy Policy",
    blocks: [
      {
        type: "p",
        text: "This Privacy Policy applies to personal information processed by RealEsy through the Services, including information associated with:",
      },
      {
        type: "list",
        items: [
          "RealEsy accounts;",
          "Vendors and service providers;",
          "Brokerage companies;",
          "office managers, administrators, and other brokerage team members;",
          "real estate agents;",
          "installers and field personnel;",
          "warehouse personnel;",
          "designers;",
          "customers and end customers;",
          "authorized users;",
          "website visitors; and",
          "other individuals whose information is processed through the Services.",
        ],
      },
      {
        type: "p",
        text: "RealEsy is a business software platform used by organizations and professionals to manage business activities, transactions, orders, services, installations, inventory, communications, and related operations.",
      },
      {
        type: "p",
        text: "In some circumstances, RealEsy determines how and why personal information is processed.",
      },
      {
        type: "p",
        text: "In other circumstances, a business or organization using RealEsy may provide, upload, or manage personal information through the Services. In those circumstances, the applicable business or organization may determine how that information is used, and RealEsy may process the information on its behalf in connection with providing the Services.",
      },
    ],
  },
  {
    id: "information-we-collect",
    title: "2. Personal Information We Collect",
    blocks: [
      {
        type: "p",
        text: "The information RealEsy collects depends on how you interact with the Services, the features you use, your role, and the information provided by you or an organization with which you are associated.",
      },
      { type: "p", text: "We may collect the following categories of information." },
    ],
    subsections: [
      {
        title: "2.1 Account and Contact Information",
        blocks: [
          { type: "p", text: "We may collect information such as:" },
          {
            type: "list",
            items: [
              "name;",
              "email address;",
              "telephone number;",
              "mailing or business address;",
              "username;",
              "account identifiers;",
              "profile information;",
              "company or organization name;",
              "brokerage information;",
              "job title or role;",
              "account permissions; and",
              "authentication and account-security information.",
            ],
          },
        ],
      },
      {
        title: "2.2 Business and Organization Information",
        blocks: [
          {
            type: "p",
            text: "If you use RealEsy on behalf of a business, brokerage, vendor, service provider, or other organization, we may collect information concerning that organization and your relationship with it, including:",
          },
          {
            type: "list",
            items: [
              "organization name;",
              "business contact information;",
              "office information;",
              "team membership;",
              "agent or employee associations;",
              "roles and permissions;",
              "billing information;",
              "service preferences; and",
              "other business information necessary to provide the Services.",
            ],
          },
        ],
      },
      {
        title: "2.3 Order, Transaction and Service Information",
        blocks: [
          {
            type: "p",
            text: "RealEsy may process information relating to transactions and business operations managed through the Services, including:",
          },
          {
            type: "list",
            items: [
              "orders;",
              "service orders;",
              "quotes;",
              "estimates;",
              "proofs and approvals;",
              "installation requests;",
              "removal requests;",
              "scheduling information;",
              "job status;",
              "inventory information;",
              "warehouse activity;",
              "shipping information;",
              "tracking information;",
              "delivery information;",
              "customer information associated with orders;",
              "transaction history;",
              "payment status;",
              "invoices;",
              "account balances; and",
              "related business records.",
            ],
          },
        ],
      },
      {
        title: "2.4 Payment and Billing Information",
        blocks: [
          {
            type: "p",
            text: "RealEsy may collect and process billing and payment-related information in connection with Subscription Plans, Add-Ons, custom plans, professional services, and other amounts payable directly to RealEsy.",
          },
          {
            type: "p",
            text: "For recurring RealEsy subscriptions and other recurring charges, a payment method may be securely saved through RealEsy's third-party payment processor to enable automatic renewals and future authorized charges. Depending on the payment processor and payment method, RealEsy may receive and maintain information such as:",
          },
          {
            type: "list",
            items: [
              "payment-method or customer identifiers;",
              "card brand;",
              "limited card details, such as the last four digits;",
              "expiration information;",
              "billing name and billing address;",
              "transaction identifiers;",
              "payment status;",
              "payment dates and amounts; and",
              "other information necessary to administer billing and recurring payments.",
            ],
          },
          {
            type: "p",
            text: "Complete payment-card numbers, card security codes, and similar sensitive payment credentials may be collected and stored by the applicable third-party payment processor rather than directly by RealEsy.",
          },
          {
            type: "p",
            text: "For transactions between Vendors or Service Providers and their customers, payments are processed through the applicable Vendor's or Service Provider's connected third-party payment processor account. RealEsy does not store complete payment-card information for those transactions and does not store or control the Vendor's or Service Provider's underlying payment credentials merely because the payment processor is connected to the Services.",
          },
          {
            type: "p",
            text: "RealEsy may receive limited transaction information necessary to provide platform functionality, such as transaction identifiers, payment status, amounts, dates, refunds, and other transaction-related information.",
          },
          {
            type: "p",
            text: "Third-party payment processors process payment information in accordance with their own privacy policies, security practices, and contractual obligations.",
          },
        ],
      },
    ],
  },
  {
    id: "uploaded-content",
    title: "3. Photos, Files and Other Uploaded Content",
    blocks: [
      {
        type: "p",
        text: "Users may upload, capture, create, or store content through the Services, including:",
      },
      {
        type: "list",
        items: [
          "installation photographs;",
          "removal photographs;",
          "inventory photographs;",
          "property or job-site photographs;",
          "artwork;",
          "designs;",
          "proofs;",
          "logos;",
          "documents;",
          "attachments;",
          "order-related files; and",
          "other business content.",
        ],
      },
      {
        type: "p",
        text: "This information may be stored as part of the applicable account, order, job, transaction, inventory record, or other business record.",
      },
      {
        type: "p",
        text: "Users and organizations are responsible for ensuring that they have appropriate authority to upload or provide personal information and other content through the Services.",
      },
    ],
  },
  {
    id: "location",
    title: "4. Location Information",
    blocks: [
      {
        type: "p",
        text: "Certain RealEsy features may use device location information to provide location-related functionality.",
      },
      { type: "p", text: "For example, location information may be used in connection with:" },
      {
        type: "list",
        items: [
          "job locations;",
          "installation or removal activities;",
          "maps;",
          "routing;",
          "address identification;",
          "field-service activities; or",
          "other location-dependent features.",
        ],
      },
      {
        type: "p",
        text: "The RealEsy mobile application may request access to your device's location when you use a feature requiring location information.",
      },
      {
        type: "p",
        text: "RealEsy does not currently use the mobile application to continuously track your location in the background when you are not using location-related functionality.",
      },
      { type: "p", text: "You may control location permissions through your device settings." },
      {
        type: "p",
        text: "Some location-based functionality may not operate correctly if location access is disabled.",
      },
      {
        type: "p",
        text: "Where reasonably available, users may be able to provide an address or other location information manually instead of providing device location.",
      },
    ],
  },
  {
    id: "technical-information",
    title: "5. Device, Usage and Technical Information",
    blocks: [
      {
        type: "p",
        text: "When you access the Services, RealEsy and its service providers may automatically collect certain technical information, including:",
      },
      {
        type: "list",
        items: [
          "Internet Protocol (IP) address;",
          "browser type;",
          "device type;",
          "operating system;",
          "application version;",
          "device identifiers;",
          "date and time of access;",
          "pages, screens, or features accessed;",
          "session information;",
          "referring pages;",
          "application performance information;",
          "error information;",
          "diagnostic information;",
          "security logs; and",
          "other technical information concerning use of the Services.",
        ],
      },
      {
        type: "p",
        text: "We use this information to operate, maintain, secure, troubleshoot, analyze, and improve the Services.",
      },
    ],
  },
  {
    id: "cookies",
    title: "6. Cookies and Similar Technologies",
    blocks: [
      {
        type: "p",
        text: "RealEsy may use cookies and similar technologies in connection with its website and web-based Services.",
      },
      { type: "p", text: "These technologies may be used to:" },
      {
        type: "list",
        items: [
          "maintain user sessions;",
          "authenticate users;",
          "remember preferences;",
          "maintain security;",
          "prevent fraud or unauthorized access;",
          "understand use of the Services;",
          "measure performance; and",
          "improve functionality.",
        ],
      },
      {
        type: "p",
        text: "RealEsy does not currently use personal information collected through the Services for third-party behavioral advertising.",
      },
      {
        type: "p",
        text: "Browser settings may allow you to block or delete cookies. Disabling certain necessary cookies may prevent portions of the Services from functioning correctly.",
      },
    ],
  },
  {
    id: "analytics",
    title: "7. Analytics",
    blocks: [
      {
        type: "p",
        text: "RealEsy uses analytics technologies, including Google Analytics, to understand how users interact with the Services and to improve performance, usability, and functionality.",
      },
      { type: "p", text: "Analytics providers may process information such as:" },
      {
        type: "list",
        items: [
          "device information;",
          "browser information;",
          "IP address;",
          "approximate location derived from IP address;",
          "pages or screens viewed;",
          "interactions with features;",
          "session information; and",
          "other usage information.",
        ],
      },
      {
        type: "p",
        text: "Information collected through analytics technologies is used for operational, analytical, security, and product-improvement purposes and not by RealEsy for selling personal information.",
      },
      {
        type: "p",
        text: "RealEsy may add, replace, or discontinue analytics or application-monitoring providers as its technology and Services evolve. Material changes to our privacy practices will be reflected in this Privacy Policy where appropriate.",
      },
    ],
  },
  {
    id: "how-we-use",
    title: "8. How We Use Personal Information",
    blocks: [
      { type: "p", text: "RealEsy may use personal information to:" },
      {
        type: "list",
        items: [
          "create and maintain accounts;",
          "authenticate users;",
          "manage user roles and permissions;",
          "provide and operate the Services;",
          "process orders and service requests;",
          "facilitate quotes, approvals, installations, removals, shipping, inventory, and other business workflows;",
          "provide maps, routing, and location-related functionality;",
          "process RealEsy subscriptions and other amounts payable to RealEsy;",
          "facilitate integrations selected or used by customers;",
          "communicate regarding accounts, transactions, services, security, support, and updates;",
          "provide customer support;",
          "troubleshoot problems;",
          "monitor performance;",
          "detect, investigate, and prevent fraud, abuse, unauthorized access, and security incidents;",
          "maintain system and account security;",
          "analyze use of the Services;",
          "improve existing features;",
          "develop new functionality;",
          "maintain business and financial records;",
          "enforce our Terms of Service and other agreements;",
          "protect RealEsy, our users, and third parties;",
          "comply with legal and regulatory requirements; and",
          "establish, exercise, or defend legal claims.",
        ],
      },
      {
        type: "p",
        text: "We may also use information for other purposes disclosed to you at the time the information is collected or with your authorization.",
      },
    ],
  },
  {
    id: "how-we-disclose",
    title: "9. How We Disclose Personal Information",
    blocks: [
      {
        type: "p",
        text: "RealEsy does not sell personal information to data brokers or other third parties for monetary consideration.",
      },
      { type: "p", text: "We may disclose personal information in the circumstances described below." },
    ],
    subsections: [
      {
        title: "9.1 Within Organizations",
        blocks: [
          {
            type: "p",
            text: "Information may be available to other authorized users within the same organization depending on account structure, roles, and permissions.",
          },
          {
            type: "p",
            text: "For example, brokerage administrators may have access to information concerning agents associated with their brokerage, and Vendor administrators may have access to information concerning orders, employees, customers, installers, and other authorized users associated with their operations.",
          },
        ],
      },
      {
        title: "9.2 Between Participants in a Transaction or Service",
        blocks: [
          {
            type: "p",
            text: "Information may be disclosed to users or organizations involved in a transaction, order, service request, installation, delivery, or other workflow when reasonably necessary to complete that activity.",
          },
          { type: "p", text: "This may include, as applicable:" },
          {
            type: "list",
            items: [
              "Vendors;",
              "Service Providers;",
              "Brokerage Companies;",
              "agents;",
              "installers;",
              "warehouse personnel;",
              "customers;",
              "shipping carriers; and",
              "other authorized participants.",
            ],
          },
          {
            type: "p",
            text: "Only information reasonably relevant to the applicable activity should be made available based on the functionality and permissions of the Services.",
          },
        ],
      },
      {
        title: "9.3 Service Providers and Technology Partners",
        blocks: [
          {
            type: "p",
            text: "RealEsy may use third-party companies to help operate and provide the Services.",
          },
          {
            type: "p",
            text: "Depending on the Services and features being used, these providers may include providers of:",
          },
          {
            type: "list",
            items: [
              "cloud infrastructure and hosting;",
              "database services;",
              "payment processing;",
              "shipping and carrier services;",
              "tax calculation services;",
              "mapping and location services;",
              "email delivery;",
              "push notifications;",
              "analytics;",
              "application monitoring;",
              "security;",
              "customer support; and",
              "other technology and operational services.",
            ],
          },
          {
            type: "p",
            text: "These providers may process personal information only as reasonably necessary to provide their services to RealEsy or as otherwise permitted by applicable law and their agreements with RealEsy.",
          },
          {
            type: "p",
            text: "Examples of providers or technologies used in connection with RealEsy may include Amazon Web Services (AWS), MongoDB Atlas, Stripe, UPS, TaxCloud, Google Maps services, and Google Analytics.",
          },
          {
            type: "p",
            text: "The particular providers used by RealEsy may change as our Services and technology evolve.",
          },
        ],
      },
      {
        title: "9.4 Legal and Safety Disclosures",
        blocks: [
          {
            type: "p",
            text: "RealEsy may disclose personal information when we reasonably believe disclosure is necessary to:",
          },
          {
            type: "list",
            items: [
              "comply with applicable law, regulation, subpoena, court order, or legal process;",
              "respond to lawful requests from governmental authorities;",
              "investigate fraud or unlawful activity;",
              "enforce our agreements;",
              "protect the security or integrity of the Services;",
              "protect the rights, property, or safety of RealEsy, our users, or others; or",
              "establish, exercise, or defend legal claims.",
            ],
          },
        ],
      },
      {
        title: "9.5 Business Transactions",
        blocks: [
          {
            type: "p",
            text: "If RealEsy is involved in a merger, acquisition, financing, restructuring, sale of assets, change of control, bankruptcy, or similar corporate transaction, personal information may be disclosed or transferred as part of that transaction, subject to applicable law.",
          },
        ],
      },
    ],
  },
  {
    id: "third-party-services",
    title: "10. Third-Party Services and Integrations",
    blocks: [
      {
        type: "p",
        text: "The Services may connect with or provide access to third-party products and services.",
      },
      {
        type: "p",
        text: "Examples may include payment processors, shipping carriers, mapping services, accounting systems, tax services, and other integrations.",
      },
      {
        type: "p",
        text: "When you or your organization chooses to use an integration, information may be transmitted between RealEsy and the applicable third party as necessary to provide the integration.",
      },
      {
        type: "p",
        text: "Third-party services operate under their own terms, privacy policies, and security practices. RealEsy does not control the independent privacy practices of third parties.",
      },
      {
        type: "p",
        text: "Users and organizations should review the privacy policies of third-party services they choose to use.",
      },
    ],
  },
  {
    id: "communications",
    title: "11. Communications and Push Notifications",
    blocks: [
      {
        type: "p",
        text: "RealEsy may use your contact information to send communications concerning:",
      },
      {
        type: "list",
        items: [
          "account activity;",
          "orders;",
          "service requests;",
          "installations;",
          "removals;",
          "transactions;",
          "invoices;",
          "subscriptions;",
          "security;",
          "support;",
          "maintenance;",
          "product updates; and",
          "other operational matters relating to the Services.",
        ],
      },
      {
        type: "p",
        text: "The RealEsy mobile application may also send push notifications where you have enabled them.",
      },
      {
        type: "p",
        text: "You may control push-notification permissions through your device settings.",
      },
      {
        type: "p",
        text: "RealEsy does not currently use SMS text messaging as a standard feature of the Services.",
      },
      {
        type: "p",
        text: "If SMS functionality is introduced in the future, this Privacy Policy and any required disclosures or consent mechanisms may be updated as appropriate.",
      },
    ],
  },
  {
    id: "data-security",
    title: "12. Data Security",
    blocks: [
      {
        type: "p",
        text: "RealEsy uses reasonable administrative, technical, and organizational safeguards designed to protect personal information against unauthorized access, loss, misuse, alteration, or disclosure.",
      },
      {
        type: "p",
        text: "These measures may include access controls, authentication mechanisms, encrypted communications, infrastructure security, monitoring, backups, and other security practices appropriate to the nature of the information and Services.",
      },
      {
        type: "p",
        text: "However, no Internet transmission, software platform, database, or electronic storage system can be guaranteed to be completely secure.",
      },
      {
        type: "p",
        text: "Users are responsible for protecting their account credentials and should promptly notify RealEsy if they suspect unauthorized access to their account.",
      },
    ],
  },
  {
    id: "data-retention",
    title: "13. Data Retention",
    blocks: [
      {
        type: "p",
        text: "RealEsy retains personal information for as long as reasonably necessary to:",
      },
      {
        type: "list",
        items: [
          "provide the Services;",
          "maintain active accounts;",
          "fulfill transactions and service requests;",
          "maintain business records;",
          "comply with legal, tax, accounting, and regulatory obligations;",
          "resolve disputes;",
          "enforce agreements;",
          "prevent fraud and abuse;",
          "maintain security; and",
          "establish, exercise, or defend legal claims.",
        ],
      },
      {
        type: "p",
        text: "Different categories of information may be retained for different periods depending on the nature of the information and the reason it is maintained.",
      },
      {
        type: "p",
        text: "Following a valid account-deletion request, RealEsy generally intends to delete or de-identify personal information associated with the account within 30 days, except for information that RealEsy is required or permitted to retain for legitimate purposes, including:",
      },
      {
        type: "list",
        items: [
          "legal compliance;",
          "tax and accounting requirements;",
          "transaction and financial recordkeeping;",
          "fraud prevention;",
          "security;",
          "dispute resolution;",
          "enforcement of agreements;",
          "establishment or defense of legal claims; or",
          "other purposes permitted by applicable law.",
        ],
      },
      {
        type: "p",
        text: "Information retained for these purposes may be restricted from ordinary use where appropriate and retained only for as long as reasonably necessary for the applicable purpose.",
      },
      {
        type: "p",
        text: "Residual copies may temporarily remain in backups, disaster-recovery systems, logs, or archives until overwritten or deleted through RealEsy's ordinary retention cycles, subject to applicable law.",
      },
      {
        type: "p",
        text: "Aggregated or de-identified information that can no longer reasonably be associated with an identifiable individual may be retained and used as permitted by law.",
      },
    ],
  },
  {
    id: "account-deletion",
    title: "14. Account and Data Deletion",
    blocks: [
      {
        type: "p",
        text: "Users may request deletion of their RealEsy account and associated personal information.",
      },
      {
        type: "p",
        text: "Where account creation is available through the RealEsy mobile application, RealEsy will provide an accessible method within the application to initiate account deletion.",
      },
      {
        type: "p",
        text: "RealEsy will also maintain an appropriate method for users to request account deletion outside the mobile application, such as through the RealEsy website or another designated web-based method.",
      },
      {
        type: "p",
        text: "RealEsy may take reasonable steps to verify the identity and authority of the person requesting deletion before processing the request.",
      },
      {
        type: "p",
        text: "Deleting an account is different from temporarily disabling, suspending, or deactivating an account.",
      },
      {
        type: "p",
        text: "Following a valid deletion request, RealEsy will delete or de-identify personal information associated with the account in accordance with Section 13, except for information that must or may lawfully be retained for legitimate business or legal purposes.",
      },
      {
        type: "p",
        text: "Deletion of an individual user's account may not require deletion of business records that belong to or must be maintained for an organization using RealEsy, provided that the individual's personal information is handled in accordance with applicable law.",
      },
      { type: "p", text: "For assistance with account or data deletion, users may contact:" },
      { type: "email" },
    ],
  },
  {
    id: "privacy-choices",
    title: "15. Your Privacy Choices",
    blocks: [
      {
        type: "p",
        text: "Depending on the Services you use, your account settings, and applicable law, you may be able to:",
      },
      {
        type: "list",
        items: [
          "access certain account information;",
          "correct or update account information;",
          "control certain profile information;",
          "control device permissions;",
          "disable location permissions;",
          "disable push notifications;",
          "manage certain cookie settings;",
          "request account deletion; or",
          "contact RealEsy concerning your personal information.",
        ],
      },
      {
        type: "p",
        text: "Some requests may be subject to identity verification and exceptions permitted by applicable law.",
      },
    ],
  },
  {
    id: "state-privacy-rights",
    title: "16. U.S. State Privacy Rights",
    blocks: [
      {
        type: "p",
        text: "Residents of certain U.S. states may have additional rights concerning their personal information under applicable state privacy laws.",
      },
      {
        type: "p",
        text: "Depending on the law and whether it applies to RealEsy or a particular processing activity, these rights may include the right to request:",
      },
      {
        type: "list",
        items: [
          "confirmation of whether personal information is being processed;",
          "access to personal information;",
          "correction of inaccurate personal information;",
          "deletion of personal information;",
          "a copy or portability of certain personal information; and",
          "information concerning certain disclosures of personal information.",
        ],
      },
      {
        type: "p",
        text: "Some state laws also provide rights concerning the sale of personal information, targeted advertising, or certain forms of profiling.",
      },
      {
        type: "p",
        text: "RealEsy does not currently sell personal information to data brokers or other third parties for monetary consideration and does not currently use personal information collected through the Services for third-party targeted behavioral advertising.",
      },
      {
        type: "p",
        text: "Privacy rights vary by jurisdiction and may be subject to exemptions, limitations, verification requirements, or applicability thresholds.",
      },
      { type: "p", text: "To submit a privacy request, contact:" },
      { type: "email" },
      {
        type: "p",
        text: "RealEsy will not unlawfully discriminate against an individual for exercising an applicable privacy right.",
      },
      {
        type: "p",
        text: "Where applicable law provides a right to appeal RealEsy's decision concerning a privacy request, instructions for submitting an appeal will be provided with RealEsy's response.",
      },
    ],
  },
  {
    id: "customer-data",
    title: "17. Information Processed on Behalf of RealEsy Customers",
    blocks: [
      {
        type: "p",
        text: "Businesses and organizations using RealEsy may upload or process personal information concerning their own employees, agents, customers, contractors, installers, service providers, or other individuals.",
      },
      {
        type: "p",
        text: "Where RealEsy processes personal information on behalf of such an organization, the organization may be responsible for determining the purposes and means of processing that information.",
      },
      {
        type: "p",
        text: "If your personal information was provided to RealEsy by a Vendor, Brokerage Company, Service Provider, employer, or other RealEsy customer, you may need to direct certain privacy requests to that organization.",
      },
      {
        type: "p",
        text: "RealEsy may assist its customers in responding to applicable privacy requests as required by law or contract.",
      },
    ],
  },
  {
    id: "childrens-privacy",
    title: "18. Children's Privacy",
    blocks: [
      {
        type: "p",
        text: "The Services are intended for business and professional users who are 18 years of age or older.",
      },
      {
        type: "p",
        text: "RealEsy does not knowingly offer accounts to children under 18 or knowingly collect personal information directly from children for the purpose of providing them with RealEsy accounts.",
      },
      {
        type: "p",
        text: "If RealEsy becomes aware that personal information has been collected directly from a child in a manner inconsistent with applicable law, RealEsy may take reasonable steps to delete or otherwise appropriately handle the information.",
      },
      {
        type: "p",
        text: "If you believe a child has provided personal information directly to RealEsy, please contact:",
      },
      { type: "email" },
    ],
  },
  {
    id: "international",
    title: "19. International Data Processing",
    blocks: [
      { type: "p", text: "RealEsy is based in the United States." },
      {
        type: "p",
        text: "Personal information processed through the Services may be stored or processed in the United States or other locations where RealEsy's service providers operate.",
      },
      {
        type: "p",
        text: "Data-protection laws in those locations may differ from the laws of your jurisdiction.",
      },
      {
        type: "p",
        text: "Where applicable law requires specific safeguards for international transfers of personal information, RealEsy will use appropriate measures as required by applicable law.",
      },
    ],
  },
  {
    id: "do-not-track",
    title: "20. Do Not Track and Browser Privacy Signals",
    blocks: [
      { type: "p", text: "Some browsers and devices provide privacy preference signals." },
      {
        type: "p",
        text: "Because standards and legal requirements concerning these signals continue to develop, RealEsy will respond to legally recognized privacy preference signals where required by applicable law and technically applicable to RealEsy's processing activities.",
      },
      {
        type: "p",
        text: "Because RealEsy does not currently use personal information collected through the Services for third-party targeted behavioral advertising or sell personal information to data brokers, certain advertising-related opt-out signals may not apply to RealEsy's current practices.",
      },
    ],
  },
  {
    id: "changes",
    title: "21. Changes to This Privacy Policy",
    blocks: [
      { type: "p", text: "RealEsy may update this Privacy Policy from time to time to reflect:" },
      {
        type: "list",
        items: [
          "changes to the Services;",
          "new features;",
          "changes to technology or service providers;",
          "changes to our data practices;",
          "legal or regulatory developments; or",
          "other operational requirements.",
        ],
      },
      {
        type: "p",
        text: "When we make changes, we will update the Effective Date at the top of this Privacy Policy.",
      },
      {
        type: "p",
        text: "If changes materially affect how we collect, use, or disclose personal information, RealEsy may provide additional notice through the Services, by email, or through another appropriate method as required by applicable law.",
      },
      {
        type: "p",
        text: "Continued use of the Services after an updated Privacy Policy becomes effective is subject to applicable law and any consent requirements that may apply.",
      },
    ],
  },
  {
    id: "contact-us",
    title: "22. Contact Us",
    blocks: [
      {
        type: "p",
        text: "Questions, concerns, privacy requests, and account-deletion requests may be directed to:",
      },
      {
        type: "address",
        lines: [
          "RealEsy, LLC",
          "2397 Church Rd",
          "Atlanta, GA 30339, USA",
          "Email: help@realesy.com",
          "Website: www.realesy.com",
        ],
      },
      {
        type: "p",
        text: "Please include sufficient information for RealEsy to understand and respond to your request. RealEsy may request additional information where reasonably necessary to verify your identity, authority, or account before fulfilling certain privacy requests.",
      },
    ],
  },
];

const renderAddressLine = (line: string) => {
  if (line.startsWith("Email: ")) {
    const email = line.replace("Email: ", "");
    return (
      <>
        Email:{" "}
        <a href={`mailto:${email}`} className="text-blue-600 hover:text-blue-700 hover:underline">
          {email}
        </a>
      </>
    );
  }
  if (line.startsWith("Website: ")) {
    const site = line.replace("Website: ", "");
    return (
      <>
        Website:{" "}
        <a
          href={`https://${site}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-700 hover:underline"
        >
          {site}
        </a>
      </>
    );
  }
  return line;
};

const renderBlock = (block: Block, key: number) => {
  switch (block.type) {
    case "p":
      return (
        <p key={key} className="text-gray-700 text-base md:text-lg leading-relaxed">
          {block.text}
        </p>
      );
    case "list":
      return (
        <ul key={key} className="list-disc pl-6 space-y-1.5 text-gray-700 text-base md:text-lg leading-relaxed marker:text-blue-600">
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );
    case "email":
      return (
        <p key={key} className="text-base md:text-lg">
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
          >
            {CONTACT_EMAIL}
          </a>
        </p>
      );
    case "address":
      return (
        <div
          key={key}
          className="bg-blue-50 border-l-4 border-blue-600 rounded-r-lg p-6 text-gray-800 text-base md:text-lg leading-relaxed"
        >
          {block.lines.map((line) => (
            <div key={line}>{renderAddressLine(line)}</div>
          ))}
        </div>
      );
    default:
      return null;
  }
};

const renderSection = (section: Section) => (
  <section key={section.id} id={section.id} className="scroll-mt-28">
    <div className="border-t border-gray-200 pt-10">
      <h2 className="font-medium text-2xl text-gray-900 mb-6">{section.title}</h2>
      <div className="space-y-5">{section.blocks.map((block, i) => renderBlock(block, i))}</div>

      {section.subsections?.map((sub) => (
        <div key={sub.title} className="mt-8">
          <h3 className="font-medium text-xl text-gray-900 mb-4">{sub.title}</h3>
          <div className="space-y-5">{sub.blocks.map((block, i) => renderBlock(block, i))}</div>
        </div>
      ))}
    </div>
  </section>
);

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-700 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl text-white">{TITLE}</h1>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6">
          <article className="max-w-4xl mx-auto space-y-12">
            <div className="space-y-5">{intro.map((text, i) => renderBlock({ type: "p", text }, i))}</div>

            {sections.map(renderSection)}
          </article>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
